import React, { useCallback, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import {
  Box,
  Button,
  Column,
  DataTable,
  Link,
  Modal,
  Svg,
  Text,
  Tooltip,
  variables,
} from '@hopskipdrive/react-components';
import { endOfToday, startOfToday } from 'date-fns';
import { uniqueId } from 'lodash';

import { Info } from '../../components/shared/Icons';
import { REFRESH_BANNER_HEIGHT } from '../../constants/layout';
import { useAuth } from '../../contexts/AuthContext';
import { UserStateProvider } from '../../contexts/UserStateContext';
import { useClose } from '../../hooks/useClose';
import { useSnooze } from '../../hooks/useSnooze';
import { useTableActions } from '../../hooks/useTableActions';
import { useUpdateStatus } from '../../hooks/useUpdateStatus';
import { useWebSocket } from '../../hooks/useWebSocket';
import { calculateAgeInMinutes, formatInTimezone } from '../../utils/helpers/date';

import { getRowHighPriorityColor, getTimePassed, isSupervisor } from './Alerts.helpers';
import {
  AlertsContainer,
  AlertsTablePanel,
  DataTableContainer,
  ReassignNameText,
  ReassignText,
  ServiceChip,
} from './Alerts.styles';
import { AssignAllCheckBox, AssignCheckBox, BulkAssignAlerts } from './BulkAssign';
import { CloseAlertModal } from './CloseAlertModal';
import FilterNav from './FilterNav';
import { IdleTimer } from './IdleTimer';
import generateIncidents from './incidentsFaker';
import { RefreshBanner } from './RefreshBanner';
import { SupervisorParams, UserParams } from './types';
import { useAlerts } from './useAlerts';
import { useAlertTypes } from './useAlertsTypes';
import { useBulkAssignAlerts } from './useAssignAlerts';

const RideStatusMapping = {
  Live: ['checked_in', 'in_progress', 'waiting'],
  Canceled: ['canceled'],
  Inactive: ['inactive'],
  Completed: ['complete'],
  Blank: ['unclaimed', 'claimed'],
};

type Params = UserParams | SupervisorParams;

const Alerts: React.FC = () => {
  const location = useLocation();
  const urlSearchParams = new URLSearchParams(location.search);
  const assignees = urlSearchParams.get('handlers')?.split(',') || undefined;
  const types = urlSearchParams.get('types') ? urlSearchParams.get('types')?.split(',') : [];
  const snoozed = urlSearchParams.get('snoozed') === 'true';
  const debugMode = urlSearchParams.get('debugMode') === 'true' && process.env.NODE_ENV === 'development';
  const [params, setParams] = useState<Params>({
    created_after: startOfToday().toISOString(),
    created_before: endOfToday().toISOString(),
    incident_types: types,
    assignees: assignees,
    snoozed: snoozed,
  });
  const { alertTypeOptions, loading: loadingAlertTypes } = useAlertTypes();
  const fakeAlerts = debugMode ? generateIncidents(200) : [];
  const { alerts: originalAlerts, setAlerts, loading, fetchAlerts, employees, markResolved } = useAlerts();
  const { lastJsonMessage: incidentStateChange } = useWebSocket({ topic: 'incidents' });
  const alerts = debugMode ? fakeAlerts : originalAlerts;
  const {
    selectedAlertIds,
    handleSelectAll,
    handleCheckboxSelection,
    handleAssignAlerts,
    assignAlertsResponse,
    reset,
    pruneIds,
    isLoading,
    isCheckAll,
    setIsCheckAll,
  } = useBulkAssignAlerts();
  const [displayedAlerts, setDisplayedAlerts] = useState<Alerts[]>(alerts);
  const [selectableAlerts, setSelectableAlerts] = useState<Alerts[]>();
  const [updateAlertsTable, setUpdateAlertsTable] = useState(true);
  const [isBannerVisible, setIsBannerVisible] = useState<boolean>(false);
  const [enableIdleTimer, setEnableIdleTimer] = useState<boolean>(false);
  const [offlineDialogOpen, setOfflineDialogOpen] = useState<boolean>(false);

  const { user } = useAuth();
  const { snoozeAlert } = useSnooze();
  const { closeAlert } = useClose();
  const { userState, handleUpdateUserStatus } = useUpdateStatus(user, employees);
  const [closeAlertId, setCloseAlertId] = useState<number>();
  const [closeAlertType, setCloseAlertType] = useState<string>('');
  const isSupervisorUser = isSupervisor(user);

  const handleSnoozeAlert = async (id) => {
    await snoozeAlert(id);
    setUpdateAlertsTable(true);
    setAlerts((prev) => prev.filter((alert) => alert.id !== id));
    pruneIds([id]);
  };

  const onAlertClosed = async (id, success) => {
    if (success) {
      setUpdateAlertsTable(true);
      setAlerts((prev) => prev.filter((alert) => alert.id !== id));
      pruneIds([id]);
    }
  };

  const actionsColumn = useTableActions({ user, handleSnoozeAlert, setCloseAlertId, setCloseAlertType });

  const handleRefreshClick = () => {
    setUpdateAlertsTable(true);
    fetchAlerts(params);
    setIsBannerVisible(false);
  };

  const handleBannerVisible = useCallback(() => setIsBannerVisible(true), []);

  const handleAlertsRemoved = useCallback(
    (resolvedIds: number[]) => {
      setDisplayedAlerts((prev) => prev.filter((alert) => !resolvedIds.includes(alert.id)));
      setAlerts((prev) => prev.filter((alert) => !resolvedIds.includes(alert.id)));
      setSelectableAlerts((prev) => prev?.filter((alert) => !resolvedIds.includes(alert.id)));
    },
    [setAlerts]
  );

  useEffect(() => {
    if (incidentStateChange?.data && incidentStateChange.topic === 'incidents') {
      const resolvedIds = incidentStateChange.data
        .flatMap((batch) => batch.incidents ?? [])
        .filter((incident) => incident.state === 'resolved')
        .map((incident) => incident.id);

      if (resolvedIds.length > 0) {
        handleAlertsRemoved(resolvedIds);
        pruneIds(resolvedIds);
        markResolved(resolvedIds);
      }
    }
  }, [incidentStateChange, handleAlertsRemoved, pruneIds, markResolved]);

  useEffect(() => {
    if (updateAlertsTable && !loading && !loadingAlertTypes) {
      setDisplayedAlerts(alerts);
      setSelectableAlerts(alerts.filter((alert) => isSupervisorUser || (!isSupervisorUser && alert.carePartnerRide)));

      setUpdateAlertsTable(false);
    }
  }, [alerts, loading, updateAlertsTable, loadingAlertTypes]);

  useEffect(() => {
    setUpdateAlertsTable(true);
    fetchAlerts(params);
  }, [fetchAlerts, params]);

  useEffect(() => {
    if (userState.srsAvailability) {
      setEnableIdleTimer(userState.srsAvailability.status === 'online');
    }
  }, [userState]);

  useEffect(() => {
    if (selectableAlerts) {
      setIsCheckAll(selectedAlertIds.length > 0 && selectedAlertIds.length === selectableAlerts.length);
    }
  }, [selectedAlertIds, selectableAlerts]);

  function alertsRideURL(rideSlug) {
    return `https://${window.env?.ALERTS_ADMIN_BASE_DOMAIN}/trips/${rideSlug}`;
  }

  const columns: Column[] = [
    {
      label: (
        <AssignAllCheckBox
          isCheckAll={isCheckAll}
          handleCheckboxSelection={(e) => handleSelectAll(e, selectableAlerts)}
        />
      ),
      align: 'left',
      valueGetter: (rowData) => (
        <>
          {selectableAlerts && selectableAlerts.includes(rowData) && (
            <AssignCheckBox
              data-testid={`assign-alert-checkbox-${rowData.id}`}
              id={rowData.id}
              selectedAlertIds={selectedAlertIds}
              handleCheckboxSelection={(e) => handleCheckboxSelection(e, rowData.id, selectedAlertIds)}
            />
          )}
        </>
      ),
      minWidth: '50px',
      maxWidth: '50px',
    },
    {
      label: 'Type',
      align: 'left',
      valueGetter: (rowData) => getIncidentTypeContent(rowData, alertTypeOptions),
      minWidth: '150px',
      maxWidth: '150px',
    },
    {
      key: 'tripSlug',
      label: 'Ride',
      align: 'left',
      valueSetter: (cellValue) => (
        <Link target="_blank" href={alertsRideURL(cellValue)}>
          {cellValue}
        </Link>
      ),
      minWidth: '130px',
      maxWidth: '130px',
    },
    {
      label: 'Ride Status',
      valueGetter: (rowData) => getRideStatus(rowData),
      align: 'left',
      minWidth: '110px',
      maxWidth: '110px',
    },
    {
      label: 'ETA',
      align: 'left',
      valueGetter: (rowData) => getETATimeFormat(rowData),
      minWidth: '110px',
      maxWidth: '110px',
    },
    {
      label: 'Pickup',
      align: 'left',
      minWidth: '110px',
      maxWidth: '110px',
      valueGetter: (rowData) => getPickupTimeFormat(rowData),
    },
    {
      label: 'Dropoff',
      align: 'left',
      minWidth: '110px',
      maxWidth: '110px',
      valueGetter: (rowData) => getDropffTimeFormat(rowData),
    },
    {
      key: 'regionName',
      label: 'Metro | Region',
      align: 'left',
      minWidth: '180px',
      maxWidth: '180px',
    },
    {
      label: 'CareDriver',
      align: 'left',
      minWidth: '130px',
      maxWidth: '130px',
      valueGetter: ({ driverName }) => driverName,
    },
    {
      label: 'Account',
      align: 'left',
      minWidth: '150px',
      maxWidth: '150px',
      valueGetter: (rowData) => getAccountName(rowData),
    },
    {
      key: 'passengerNames',
      label: 'Rider',
      align: 'left',
      valueSetter: (cellValue) => cellValue && cellValue.map((rider) => <Text key={uniqueId()}>{rider}</Text>),
      minWidth: '150px',
      maxWidth: '150px',
    },
    {
      label: 'SRS',
      align: 'left',
      minWidth: '100px',
      maxWidth: '100px',
      valueGetter: ({ assigneeName }) => assigneeName,
    },
    {
      label: 'Reassignment',
      align: 'left',
      valueGetter: (rowData) => getReassignmentStatus(rowData),
    },
    {
      label: 'Age',
      align: 'left',
      valueGetter: ({ createdAt }) => `${calculateAgeInMinutes(createdAt)} min`,
      minWidth: '80px',
      maxWidth: '80px',
    },
    {
      label: 'Status',
      align: 'center',
      valueGetter: ({ state }) => formatStatus(state),
      minWidth: '100px',
      maxWidth: '100px',
    },
    ...actionsColumn,
  ];

  return (
    <UserStateProvider
      userState={userState}
      handleUpdateUserStatus={handleUpdateUserStatus}
      setOfflineDialogOpen={setOfflineDialogOpen}
    >
      <AlertsContainer>
        <FilterNav employees={employees} params={params} setParams={setParams} alertTypeOptions={alertTypeOptions} />
        <Box overflow="auto" width="100%">
          <RefreshBanner
            employees={employees}
            currentAlerts={alerts}
            queryParams={params}
            onRefresh={() => handleRefreshClick()}
            onVisible={handleBannerVisible}
          />
          <AlertsTablePanel>
            {enableIdleTimer ? <IdleTimer user={user} employees={employees} /> : null}
            <TableContainer heightOffset={isBannerVisible ? REFRESH_BANNER_HEIGHT : 0}>
              <DataTable
                stickyHeader={true}
                rows={displayedAlerts}
                isLoading={loading && loadingAlertTypes}
                columns={columns}
                noRecordsMessage={getNoAlertsMessage()}
                handleRowClick={() => false}
                allowRowClick={() => false}
                handleRowStyles={(rowData) => getRowHighPriorityColor(rowData)}
              />
            </TableContainer>
            <BulkAssignAlerts
              selectedAlertIds={selectedAlertIds}
              employees={employees}
              handleAssignAlerts={async (assigneeId) => {
                await handleAssignAlerts(assigneeId);
                setParams({ ...params });
              }}
              onClose={reset}
              assignAlertsResponse={assignAlertsResponse}
              isLoading={isLoading}
            />
            {offlineDialogOpen && (
              <Modal maxWidth={'sm'} open={offlineDialogOpen}>
                <Modal.Title>You are offline</Modal.Title>
                <Modal.Content>
                  <p>Your status has been set to offline due to inactivity.</p>
                </Modal.Content>
                <Modal.Actions>
                  <Button
                    variant="text"
                    onClick={() => {
                      setOfflineDialogOpen(false);
                    }}
                  >
                    Ok
                  </Button>
                </Modal.Actions>
              </Modal>
            )}
            {user && (
              <CloseAlertModal
                userId={user.id}
                closeAlert={closeAlert}
                closeAlertType={closeAlertType}
                onAlertClosed={onAlertClosed}
                closeAlertId={closeAlertId}
                setCloseAlertId={() => setCloseAlertId(undefined)}
              />
            )}
          </AlertsTablePanel>
        </Box>
      </AlertsContainer>
    </UserStateProvider>
  );
};

export default Alerts;

const getNoAlertsMessage = () => {
  return (
    <Box display="flex" justifyContent="center" alignItems="center" height="80vh">
      <Text textColor="gray" noWrap>
        {'No Alerts Available'}
      </Text>
    </Box>
  );
};

const getETATimeFormat: React.FC<{ tripEta: string; timeZoneName: string }> = ({ tripEta, timeZoneName }) => (
  <Text>{formatInTimezone(tripEta, timeZoneName, 'h:mm aaa z')}</Text>
);

const getPickupTimeFormat: React.FC<{ tripStartsAt: string; timeZoneName: string }> = ({
  tripStartsAt,
  timeZoneName,
}) => <Text>{formatInTimezone(tripStartsAt, timeZoneName, 'h:mm aaa z')}</Text>;

const getDropffTimeFormat: React.FC<{ tripEndsAt: string; timeZoneName: string }> = ({ tripEndsAt, timeZoneName }) => (
  <Text>{formatInTimezone(tripEndsAt, timeZoneName, 'h:mm aaa z')}</Text>
);

const getAccountName: React.FC<{ tripAccountSlug: string; tripAccountName: string }> = ({
  tripAccountSlug,
  tripAccountName,
}) => (
  <Link
    target="_blank"
    href={`https://${window.env?.ALERTS_BASE_DOMAIN}/accounts/${tripAccountSlug}`}
    style={{ display: 'inline-block', minWidth: '150px', maxWidth: '150px' }}
  >
    {tripAccountName}
  </Link>
);

const formatStatus = (status) => (status === 'snoozed' ? 'Snoozed' : 'Active');

const getIncidentTypeContent = (rowData, alertTypeOptions) => {
  const highPrio = rowData.highPriority;

  return (
    <>
      {highPrio && <Svg icon="alert-warning" width={18} />}
      <Text style={{ color: highPrio ? 'rgba(124, 17, 0, 1)' : 'inherit' }}>
        {alertTypeOptions?.find((option) => option.value === rowData.incidentType)?.label}
      </Text>
      {rowData.carePartnerRide && (
        <Box display="flex" justifyContent="flex-start" gap="2px" mt={1}>
          {rowData.services.map((service) => (
            <ServiceChip key={service.code} label={service.code} />
          ))}
        </Box>
      )}
    </>
  );
};

const getRideStatus = (rowData) => {
  const status = Object.keys(RideStatusMapping).find((key) => RideStatusMapping[key].includes(rowData.rideStatus));

  return <Text>{status === 'Blank' ? '' : status}</Text>;
};

const displayNoteContent = (note) => {
  let assigner;
  let currentAssignee;
  let previousAssignee;
  let assignedTime;

  const pattern = /^(.+?)\s+reassigned\s+(.+?)\s+and unassigned\s+(.+?)\s+from/;
  const matches = note.content.match(pattern);

  if (matches) {
    assigner = matches[1];
    currentAssignee = matches[2];
    previousAssignee = matches[3];
    assignedTime = getTimePassed(new Date(), new Date(note.createdAt));
  }

  return (
    <>
      {matches ? (
        <Box display="flex" gap={0.5}>
          <ReassignNameText>{assigner}</ReassignNameText>
          <ReassignText>reassigned</ReassignText>
          <ReassignNameText>{currentAssignee}</ReassignNameText>
          <ReassignText>and unassigned</ReassignText>
          <ReassignNameText>{previousAssignee}</ReassignNameText>
          <ReassignText>{assignedTime}</ReassignText>
        </Box>
      ) : (
        <Text>{note.content}</Text>
      )}
    </>
  );
};

const getReassignmentStatus = (rowData) => {
  if (rowData.lastReassignmentNote) {
    return (
      <Box display="flex" alignItems="center" gap="4px">
        <Text>Reassigned</Text>
        <Tooltip
          title={displayNoteContent(rowData.lastReassignmentNote)}
          placement="top"
          componentsProps={{
            tooltip: {
              sx: {
                maxWidth: 'none',
                backgroundColor: 'var(--ui-color-flat-white-default, #FFF)',
                borderRadius: '5px',
                border: '1px solid var(--border-main, #CBD1D6)',
                boxShadow: '0px 2px 2px 0px rgba(0, 0, 0, 0.07)',
                color: 'var(--text-primary, #242526)',
              },
            },
            arrow: {
              sx: {
                color: variables.color.white,
              },
            },
          }}
          arrow
        >
          <div>
            <Info style={{ color: '#757575', height: '30px', width: '30px' }} />
          </div>
        </Tooltip>
      </Box>
    );
  }

  return <Text>-</Text>;
};

const TableContainer = ({ children, heightOffset }) => {
  const [dimensions, setDimensions] = useState({
    height: window.innerHeight - 36,
    width: window.innerWidth,
  });

  useEffect(() => {
    const handleResize = () => {
      setDimensions({
        height: window.innerHeight - heightOffset,
        width: window.innerWidth,
      });
    };

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [heightOffset]);

  return (
    <DataTableContainer minHeight={`${dimensions.height - (heightOffset || 0)}px`} top={heightOffset || 0}>
      {children}
    </DataTableContainer>
  );
};
